<template>
  <div class="sf-form-group sf-search__container" :class="searchContainerClass">
    <span v-if="showSearchIcon" class="fa fa-search sf-search__icon" />
    <input
      v-focus="focusedAt ? focusedAt : false"
      data-testid="search-input-input"
      class="sf-form-input-transparent sf-full sf-search__input"
      :class="searchInputClass"
      type="text"
      :value="modelValue"
      v-bind="$attrs"
      @input="debouncedSearch"
      @click.stop=""
    />
    <div
      v-if="matchesNumber"
      class="sf-label sf-label-secondary sf-nowrap sf-search__matches"
      data-testid="sf-search-matches"
    >
      {{ matchesNumberText }}
    </div>
    <div v-if="showResultNavigation" class="sf-search__navigation-container">
      <div class="sf-search__navigation-icon-container">
        <span
          class="
            fa fa-chevron-up
            sf-pointer
            sf-search__icon sf-search__icon--navigation
          "
          data-testid="sf-search-previous-result"
          @click.prevent="emitPreviousResult"
        />
      </div>
      <div class="sf-search__navigation-icon-container">
        <span
          class="
            fa fa-chevron-down
            sf-pointer
            sf-search__icon sf-search__icon--navigation
          "
          data-testid="sf-search-next-result"
          @click.prevent="emitNextResult"
        />
      </div>
    </div>
    <span
      v-if="effectiveShowClearIcon"
      class="fa fa-remove sf-pointer sf-search__icon sf-search__icon--right"
      data-testid="sf-search-clear"
      @click.prevent.stop="emitClear"
    />
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
import { computed, onBeforeUnmount, useAttrs } from 'vue'

defineOptions({
  name: 'SfSearchInput',
  inheritAttrs: false,
})

const props = defineProps({
  matchesNumber: {
    type: Number,
    default: 0,
  },
  showClearIcon: {
    type: Boolean,
    default: true,
  },
  showSearchIcon: {
    type: Boolean,
    default: true,
  },
  showResultNavigation: {
    type: Boolean,
    default: true,
  },
  modelValue: {
    type: String,
    default: '',
  },
  focusedAt: {
    type: Number,
    default: 0,
  },
  wide: {
    type: Boolean,
    default: false,
    required: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
})

const $attrs = useAttrs()

const emit =
  defineEmits<{
    'update:modelValue': [value: string | null]
    clear: []
    'next-result': []
    'previous-result': []
  }>()

const matchesNumberText = computed(() =>
  window.I18n.t('components.search.matches_number', {
    number: props.matchesNumber,
  })
)

const effectiveShowClearIcon = computed(
  () => props.showClearIcon && props.modelValue
)

const searchContainerClass = computed(() => ({
  'sf-search__container--with-navigation': props.showResultNavigation,
  'sf-search__container--wide': props.wide,
  'sf-search__container--compact': props.compact,
}))

const searchInputClass = computed(() => ({
  'sf-search__item--left-icon': props.showSearchIcon,
  'sf-search__item--right-icon': props.showClearIcon,
  'sf-search__item--with-nav': props.showResultNavigation,
  'sf-search__item--right-icon-with-nav':
    props.showClearIcon && props.showResultNavigation,
}))

const emitClear = () => emit('clear')
const emitNextResult = () => emit('next-result')
const emitPreviousResult = () => emit('previous-result')

const debouncedSearch = debounce((event: Event) => {
  emit('update:modelValue', (event.target as HTMLInputElement)?.value)
}, 300)

onBeforeUnmount(() => {
  if (debouncedSearch && typeof debouncedSearch.cancel === 'function') {
    debouncedSearch.cancel()
  }
})
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

$base-padding-horizontal: 8px;
$base-padding-vertical: 10px;
$icon-width: 16px;
$right-icon-margin: 18px;
$navigator-width: 64px;

.sf-search {
  &__container {
    @include background-color('snow', 'light');

    display: flex;
    align-items: center;
    border: 1px solid -color('snow', 'darker');
    border-radius: 8px;
    padding: 9px 7px;

    &--compact {
      padding: 7px;
    }

    &--with-navigation {
      padding: 5px 7px;
    }

    &--wide {
      width: 100%;
    }
  }

  &__icon {
    @include font-size('body', 's');
    @include color('text', 'darker');

    margin: 0 4px 0 0;
    width: 15px;
    height: 15px;
    line-height: 15px;

    &--right {
      margin: 0;
    }

    &--navigation {
      margin: 0;
    }
  }

  &__input {
    padding: 0;
    line-height: 16px;
    font-size: 14px !important;

    &--left-icon {
      padding-left: $base-padding-horizontal + $icon-width + 1;
    }

    &--right-icon {
      padding-right: $base-padding-horizontal + $icon-width + 1;
    }

    &--with-nav {
      padding-right: $base-padding-horizontal + $navigator-width + 1;
    }

    &--right-icon-with-nav {
      padding-right: $base-padding-horizontal + $icon-width + $right-icon-margin +
        $navigator-width + 1;
    }
  }

  &__matches {
    margin: 0 18px 0 0;
  }

  &__navigation-container {
    @include background-color('white');

    display: flex;
    margin: 0 18px 0 0;
    border: 1px solid -color('snow', 'darker');
    border-radius: 8px;
    box-shadow: 0 1px 0 -color('snow', 'dark');
  }

  &__navigation-icon-container {
    line-height: 16px;
    padding: 3px 8px;
    border-right: 1px solid -color('snow', 'darker');
    text-align: center;

    &:last-of-type {
      border-right: none;
    }
  }
}
</style>
